import React from 'react';
import { Body, Flex, Title } from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { ConsistencyReportSummary } from '../consistencyReportTypes';
import { formatDateRecentTime } from 'utils/misc';
import { getValidityColor } from 'utils/runStatistics';
import { calculateSuccessRate } from '../helpers';

const STATUS_COLORS: Partial<
  Record<ConsistencyReportSummary['status'], string>
> = {
  ERROR: 'var(--cogs-red)',
  IN_PROGRESS: 'var(--cogs-blue)',
};

const STATUS_LABELS: Record<ConsistencyReportSummary['status'], string> = {
  SUCCESS: 'Success',
  ERROR: 'Error',
  IN_PROGRESS: 'In Progress',
};

type ConsistencyReportCardProps = {
  report: ConsistencyReportSummary;
  onOpen: VoidFunction;
};

export const ConsistencyReportCard = ({
  report,
  onOpen,
}: ConsistencyReportCardProps) => {
  const validPercentage = calculateSuccessRate(
    report.totalValid,
    report.totalFailed
  );

  return (
    <StyledCard direction="column" onClick={onOpen}>
      <div>
        <StyledDate level={4}>{formatDateRecentTime(report.runAt)}</StyledDate>
        <StyledTitle level={3}>{report.collectionName}</StyledTitle>
      </div>

      <Body size="x-small">
        {report.schemas.length} schemas &bull; {report.rulesCount} rules
      </Body>

      <StatsRow gap={16}>
        <Body size="x-small">
          <strong>{report.totalValid}</strong> valid
        </Body>
        <Body size="x-small">
          <strong>{report.totalFailed}</strong> failed
        </Body>
      </StatsRow>

      <Flex direction="column" gap={4}>
        <StatusBadge
          size="x-small"
          statusColor={
            STATUS_COLORS[report.status] ?? getValidityColor(validPercentage)
          }
        >
          {validPercentage}% &mdash; {STATUS_LABELS[report.status]}
        </StatusBadge>
        <ProgressBar
          progressWidth={validPercentage}
          progressColor={getValidityColor(validPercentage)}
        />
      </Flex>
    </StyledCard>
  );
};

const StyledCard = styled(Flex)`
  background: var(--cogs-white);
  width: 22%;
  max-width: 400px;
  box-shadow: 0px 0px 7px var(--cogs-greyscale-grey3);
  transition: all 250ms linear;
  cursor: pointer;
  border-radius: 5px;
  padding: 1rem 1.5rem;
  gap: 0.75rem;

  &:hover {
    background: var(--cogs-greyscale-grey2);
  }
`;

const StyledDate = styled(Title)`
  margin-bottom: 0.2rem;
  color: var(--cogs-greyscale-grey7);
`;

const StyledTitle = styled(Title)`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatsRow = styled(Flex)`
  .cogs-body--x-small strong {
    font-weight: 600;
  }
`;

const StatusBadge = styled(Body)<{ statusColor: string }>`
  color: ${({ statusColor }) => statusColor};
  font-weight: 600;
`;

const ProgressBar = styled.div<{
  progressWidth: number;
  progressColor: string;
}>`
  height: 4px;
  width: 100%;
  background: var(--cogs-greyscale-grey3);
  border-radius: 2px;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ progressWidth }) => progressWidth}%;
    background: ${({ progressColor }) => progressColor};
    transition: width 0.3s ease;
  }
`;
